import React, { useContext, useState, useCallback } from 'react';
import { useEffect, useLayoutEffect } from 'react';
import { store } from 'components/Store';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';
import createNotification from 'components/Settings/Utilities/CreateNotification';
import { useRef } from 'react';
import thousandCommas from 'components/Dashboard/helpers/thousandCommas';
import CreatableSelect from 'react-select/creatable';
import AsyncCreatableSelect from 'react-select/async-creatable';
import { components } from 'react-select';
import debounce from 'components/Dashboard/helpers/debounce';
import useContactsApiCalls from 'components/Dashboard/Influence/CrmContacts/useContactsApiCalls';
import CustomHighlighter from 'components/Dashboard/utilities/CustomHighlighter';
import { useParams } from 'react-router';
import ModalComponent from 'components/Common/Modal';

function AddTagsModal() {
  const globalState = useContext(store);
  const { state, dispatch } = globalState;
  const { contentResults, setContentResults } = useContentResults();
  const { contactsTags, showAddTagsModalCrmContacts, selectedCrmContacts, crmContactsTotalHits } = state;

  const { CrmContacts } = contentResults;
  const { selectedIds, selectedAll, selectedAllVisible } = selectedCrmContacts;
  const { preselectedContact } = showAddTagsModalCrmContacts;
  const totalHits = crmContactsTotalHits;

  const [tags, setTags] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { addTagsToContacts } = useContactsApiCalls();

  const params = useParams();
  const { groupId } = params;

  const setShowAddTagsModal = (value) => {
    dispatch({
      type: 'MODIFY_SECTION',
      parameter: 'showAddTagsModalCrmContacts',
      value,
    });
  };

  const hideAddTagsModal = () => {
    setShowAddTagsModal({ show: false, preselectedContact: undefined });
  };

  const setSelectedCrmContacts = (value) => {
    dispatch({
      type: 'MODIFY_SECTION',
      parameter: 'selectedCrmContacts',
      value,
    });
  };

  const resetSelectedContacts = () => {
    setSelectedCrmContacts({
      selectedAll: false,
      selectedIds: [],
      selectedAllVisible: [],
    });
  };

  const closeModal = () => {
    hideAddTagsModal();
  };

  const contactsCount = preselectedContact
    ? 1
    : selectedAllVisible.length > 0
      ? selectedAllVisible.length
      : selectedIds.length > 0
        ? selectedIds.length
        : totalHits;

  //API CALL TO ADD TAGS TO CONTACTS
  const addTagsToContactsHandler = async () => {
    setIsLoading(true);
    const contactIds = preselectedContact
      ? [preselectedContact]
      : selectedAllVisible.length > 0
        ? selectedAllVisible
        : selectedIds;

    const newTags = tags.map((tag) => tag.value);
    let result = await addTagsToContacts({ tags: newTags, contactIds, preselectedContact });
    if (result || result?.status === 200) {
      createNotification(
        'success',
        `Tags added to ${thousandCommas(contactsCount)} contact${contactsCount > 1 ? 's' : ''} successfully`
      );
      let newCrmContacts;
      if (selectedAll && !preselectedContact) {
        newCrmContacts = CrmContacts?.hits.map((stakeholder) => {
          return {
            ...stakeholder,
            tags: [
              ...stakeholder.tags,
              ...tags.filter((tag) => !stakeholder.tags.includes(tag.value)).map((tag) => tag.value),
            ],
          };
        });
      } else {
        newCrmContacts = CrmContacts?.hits.map((stakeholder) => {
          if (contactIds.includes(stakeholder.id)) {
            return {
              ...stakeholder,
              tags: [
                ...stakeholder.tags,
                ...tags.filter((tag) => !stakeholder.tags.includes(tag.value)).map((tag) => tag.value),
              ],
            };
          } else {
            return stakeholder;
          }
        });
      }
      // ADD TAG TO GLOBAL STORE TOO, TO AVOID RELOAD CONTACTS
      setContentResults({
        ...contentResults,
        CrmContacts: {
          ...CrmContacts,
          hits: newCrmContacts,
        },
      });
      dispatch({
        type: 'MODIFY_SECTION',
        parameter: 'contactsTags',
        value: [...contactsTags, ...tags.filter((tag) => !contactsTags.find((item) => item.value === tag.value))],
      });
      if (groupId) {
        dispatch({
          type: 'MODIFY_SECTION',
          parameter: 'contactOnGroupUpdated',
          value: newCrmContacts,
        });
      }
      resetSelectedContacts();
      setTags([]);
      closeModal();
    }
    setIsLoading(false);
  };

  return (
    <ModalComponent maxWidth={790} isOpen={showAddTagsModalCrmContacts.show} setIsOpen={closeModal}>
      <div className='pt-4'>
        <AddTagsContent
          titleText={
            contactsCount > 1
              ? `${thousandCommas(contactsCount)} contacts`
              : `${
                  contentResults?.CrmContacts?.hits?.find(
                    (contact) => contact?.id === (preselectedContact ? preselectedContact : selectedIds[0])
                  )?.title
                }`
          }
          contactsCount={contactsCount}
          isLoading={isLoading}
          addTagsToContacts={addTagsToContactsHandler}
          setTags={setTags}
          tags={tags}
          closeModal={closeModal}
          searchIcon
        />
      </div>
    </ModalComponent>
  );
}
export const defaultRenderLayout = (tagComponents, inputComponent) => {
  return (
    <>
      <div className='d-flex flex-wrap align-items-center' style={{ minHeight: '36px' }}>
        {tagComponents}
        <span></span>
        <span className='flex-grow-1' style={{ flex: '1 1' }}>
          {inputComponent}
        </span>
      </div>
    </>
  );
};

const AddTagsContent = (props) => {
  const {
    titleText,
    tags,
    setTags,
    isLoading,
    addTagsToContacts,
    closeModal,
    contactsCount,
    customContact,
    onChangeCallback,
    autofocus,
    searchIcon,
    tagOptions,
    loadTagOptions,
    unitLabel = 'user',
    roundedTagChips,
    singleLine,
    onMenuOpen,
    onMenuClose,
    closeSignal,
  } = props;
  const globalState = useContext(store);
  const { state } = globalState;
  const { contactsTags } = state;

  const selectRef = useRef(null);
  const wrapperRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const getValueContainer = () => wrapperRef.current?.querySelector('.react-custom-select-tags__value-container');

  // Browsers can leave a subpixel gap between scrollLeft + clientWidth and scrollWidth even when
  // scrolled all the way to an end, so a 1px tolerance isn't always enough to call it "at the end".
  const SCROLL_END_TOLERANCE_PX = 2;
  const updateScrollState = useCallback(() => {
    const el = getValueContainer();
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > SCROLL_END_TOLERANCE_PX);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - SCROLL_END_TOLERANCE_PX);
  }, []);

  // Same left/right scroll-arrow pattern as the keyword suggestions strip, since the select's
  // chips can only be scrolled by dragging the thin native scrollbar otherwise.
  useEffect(() => {
    if (!singleLine) return;
    const el = getValueContainer();
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState);
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      observer.disconnect();
    };
  }, [singleLine, tags, updateScrollState]);

  // A rapid extra click aimed at the left arrow can land on whatever now occupies that spot once
  // the arrow disappears (from the previous click's resulting scroll state) - a chip's remove
  // button, not just the input. Blur can undo an accidental focus but not an accidental tag
  // removal, so block the spot outright with an overlay for a short window instead. Not needed on
  // the right: that space stays reserved padding either way (see valueContainer styles), so a
  // click landing there is expected, not a misfire.
  const [justScrolledLeft, setJustScrolledLeft] = useState(false);
  const scrollCooldownRef = useRef(null);
  const scrollTags = (direction) => {
    if (direction < 0) {
      setJustScrolledLeft(true);
      clearTimeout(scrollCooldownRef.current);
      scrollCooldownRef.current = setTimeout(() => setJustScrolledLeft(false), 300);
    }
    getValueContainer()?.scrollBy({ left: direction * 120, behavior: 'smooth' });
  };

  // Scroll the freshly-added chip into view - otherwise it lands off the visible edge of the
  // single-line row and looks like nothing happened.
  const prevTagsLengthRef = useRef(tags.length);
  useEffect(() => {
    if (singleLine && tags.length > prevTagsLengthRef.current) {
      requestAnimationFrame(() => {
        const el = getValueContainer();
        el?.scrollTo({ left: el.scrollWidth, behavior: 'smooth' });
      });
    }
    prevTagsLengthRef.current = tags.length;
  }, [tags, singleLine]);

  const createOption = (label) => ({
    label,
    value: label,
  });
  const [options, setOptions] = useState(tagOptions ?? contactsTags);

  const handleCreate = (inputValue) => {
    const newOption = createOption(inputValue);
    setOptions((prev) => [...prev, newOption]);
    setTags([...tags, newOption]);
  };

  const debouncedLoadOptions = useRef(
    debounce((inputValue, callback) => {
      loadTagOptions(inputValue).then((results) => callback(results ?? []));
    }, 300)
  ).current;

  const SelectComponent = loadTagOptions ? AsyncCreatableSelect : CreatableSelect;

  const colourStyles = {
    control: (styles) => ({
      ...styles,
      borderColor: 'transparent',
      outline: 'none',
      boxShadow: 'none',
      ':hover': {
        borderColor: 'transparent',
      },
      backgroundColor: 'transparent',
      cursor: 'text',
      marginLeft: customContact ? undefined : '40px',
    }),
    option: (styles, props) => {
      const newValue = props.data['__isNew__'];
      // customContact (the interaction form's inline tag field) matches the Type/Initiator
      // dropdowns' list-action-button hover instead of the bulk "Add tags" modal's blue highlight.
      const focusedBackground = customContact ? 'rgba(244, 239, 237, 0.6)' : '#0094cc';
      const focusedColor = customContact ? '#00122b' : '#fff';
      return {
        ...styles,
        // Matches the Type/Initiator list items' py-2 px-3 (Bootstrap 8px/16px).
        padding: customContact ? '8px 16px' : styles.padding,
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        backgroundColor: props.isFocused ? focusedBackground : 'transparent',
        cursor: 'pointer',
        color: props.isFocused ? focusedColor : '#00122b',
        fontSize: '16px',
        ':active': {
          ...styles[':active'],
          backgroundColor: 'transparent',
        },
        ':hover': {
          ...styles[':hover'],
          color: focusedColor,
          backgroundColor: focusedBackground,
        },
        ':focus': {
          ...styles[':focus'],
          color: newValue ? (customContact ? '#00122b' : '#0094cc') : focusedColor,
          backgroundColor: focusedBackground,
        },
      };
    },
    indicatorsContainer: (styles) => ({
      ...styles,
      backgroundColor: 'transparent',
    }),
    multiValue: (styles) => ({
      ...styles,
      backgroundColor: roundedTagChips ? undefined : 'transparent',
      flexShrink: singleLine ? 0 : undefined,
      ':hover': {
        backgroundColor: roundedTagChips ? undefined : 'transparent',
      },
    }),
    multiValueRemove: (styles) => ({
      ...styles,
      color: 'rgba(0, 18, 43, 0.4)',
      ':hover': {
        backgroundColor: '#edd5d7',
        color: '#9b171f',
      },
    }),
    valueContainer: (styles) => ({
      ...styles,
      ...(singleLine
        ? {
            flexWrap: 'nowrap',
            overflowX: 'auto',
            overflowY: 'hidden',
            paddingTop: '2px',
            paddingBottom: '2px',
            // canScrollLeft depends only on scrollLeft, so tying padding to it is safe - it can't
            // feed back into itself. canScrollRight depends on scrollWidth, which THIS padding
            // change would itself alter, so that side has to stay constant to avoid the two
            // fighting each other instead of settling (that's what caused the flapping arrow).
            paddingLeft: canScrollLeft ? '36px' : '12px',
            paddingRight: '36px',
          }
        : { maxHeight: '120px', overflow: 'auto' }),
    }),
    menu: (styles) => ({
      ...styles,
      // Matches the Type/Initiator dropdowns' .shadow-open-dropdown + .interaction-form-dropdown look.
      borderRadius: customContact ? 8 : 0,
      boxShadow: customContact ? '0 1px 3px rgba(60, 63, 66, 0.12), 0 4px 10px rgba(60, 63, 66, 0.05)' : 'none',
      border: customContact ? '1px solid rgba(212, 212, 212, 0.6)' : undefined,
      // menuList's own scrollbar sits flush against its right edge - without this, that square
      // corner pokes past the rounded corner set above instead of being clipped to match it.
      overflow: customContact ? 'hidden' : undefined,
      left: customContact ? '-1px' : undefined,
      width: customContact ? 'calc(100% + 2px)' : undefined,
      top: customContact ? 'calc(100% + 1px)' : undefined,
      marginTop: '2px',
      ...(customContact
        ? {}
        : {
            position: 'static',
            flex: 1,
            minHeight: 0,
          }),
    }),
    menuList: (styles) => ({
      ...styles,
      maxHeight: customContact ? '180px !important' : 'none',
      paddingTop: customContact ? 0 : styles.paddingTop,
      paddingBottom: customContact ? 0 : styles.paddingBottom,
      ...(customContact
        ? {}
        : {
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
          }),
    }),
    // customContact renders inline inside the modal's own scrollable content area, which clips
    // overflow (that's what makes it scrollable) - box-shadow on the menu above got cut off on the
    // sides because of it. Portaling to <body>, like the Stakeholders/Members select already does
    // in this same modal, escapes that clipping; top/left/width come from react-select's own
    // control-rect measurement, only the z-index needs bumping above the modal's own overlay.
    menuPortal: (styles) => (customContact ? { ...styles, zIndex: 100001 } : styles),
    container: (styles) => ({
      ...styles,
      borderBottom: customContact ? undefined : '0.5px solid #ececec',
      ...(customContact
        ? {}
        : {
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            height: '100%',
          }),
    }),
    placeholder: (styles) => ({
      ...styles,
      fontSize: '15px',
      marginTop: '-1px',
      // Match the People/Stakeholder selects' placeholder colour instead of react-select's
      // default grey, and never wrap - a single-line field has no room for a second line.
      color: 'rgba(0, 18, 43, 0.6)',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: '100%',
    }),
  };

  const Option = (props) => {
    const input = props.selectProps.inputValue;
    const value = props.data.value;
    const newValue = props.data['__isNew__'];
    const textRef = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);

    useLayoutEffect(() => {
      const el = textRef.current;
      if (el) setIsTruncated(el.scrollWidth > el.clientWidth);
    }, [value, newValue]);

    return (
      <components.Option {...props}>
        <div
          className='select-option-wrapper'
          ref={textRef}
          {...(isTruncated ? { 'data-tooltip-id': 'general-tooltip', 'data-tooltip-content': value } : {})}
        >
          {newValue && (
            <span className='react-select-create-tag-message'>
              <span className='icon-tag icon-tag-create-option'></span> Create new tag:&nbsp;
            </span>
          )}
          <CustomHighlighter
            highlightClassName='highlight-text'
            searchWord={input}
            textToHighlight={newValue ? `'${value}'` : value || ''}
          />
        </div>
      </components.Option>
    );
  };
  const CustomMultiValueLabel = (props) => {
    return (
      <div className='tags-select-multivalue-item'>
        <span className='icon-tag icon-tag-value-label'></span>
        <div>
          <components.MultiValueLabel {...props} />
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (selectRef.current && autofocus) {
      selectRef.current.focus();
    }
  }, [selectRef.current]);

  // Lets a parent form close this select when some other field opens, same as it would close a
  // native dropdown - closeSignal is a bump counter, unrelated to whether this select is itself
  // open, so it doesn't fight the uncontrolled menuIsOpen used in customContact mode. Blurring an
  // already-unfocused select is a no-op, so this is safe to run on mount too.
  useEffect(() => {
    if (closeSignal !== undefined) selectRef.current?.blur();
  }, [closeSignal]);

  return (
    <div className='d-flex flex-column'>
      {!customContact && (
        <h3 className='topic-title topic-title-email-alerts mb-0 section-title pb-3 px-lg-4 px-3 flex-shrink-0'>
          {`Add tag${tags.length > 1 ? 's' : ''} to ${titleText}`}
        </h3>
      )}
      <div
        ref={wrapperRef}
        className={`tags-select-creatable flex-shrink-0 ${customContact ? 'tags-select-creatable-custom-contact' : ''} ${roundedTagChips ? 'rounded-tag-chips' : ''} ${singleLine ? 'single-line-tags' : ''}`}
        style={{ height: customContact ? undefined : '350px' }}
      >
        {searchIcon && <span className={`icon-search`}></span>}
        {singleLine && canScrollLeft && (
          <button
            type='button'
            className='general-button tags-scroll-arrow direction-left'
            onClick={() => scrollTags(-1)}
          >
            <span className='tags-scroll-arrow-icon' />
          </button>
        )}
        {singleLine && !canScrollLeft && justScrolledLeft && (
          <div
            className='position-absolute'
            style={{ left: 0, top: 0, bottom: 0, width: '34px', zIndex: 3 }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          />
        )}
        <SelectComponent
          isMulti
          ref={selectRef}
          isDisabled={isLoading}
          isClearable
          onChange={(newValue) => {
            if (onChangeCallback) onChangeCallback();
            const isRemoval = (newValue?.length ?? 0) < tags.length;
            const el = getValueContainer();
            const lockedScrollLeft = el?.scrollLeft;
            setTags(newValue);
            if (isRemoval && el && lockedScrollLeft != null) {
              // Refocusing the input after removing a chip both pops the dropdown back open (blur
              // undoes that) and scrolls the row to reveal the input, which lives at the end after
              // the chips. A single delayed correction lets that scroll paint for a frame first,
              // which reads as a jump-to-end-then-back - instead, revert every scroll attempt the
              // instant it happens for a short window so the wrong position never gets painted.
              selectRef.current?.blur();
              const holdScrollPosition = () => {
                el.scrollLeft = lockedScrollLeft;
              };
              holdScrollPosition();
              el.addEventListener('scroll', holdScrollPosition);
              setTimeout(() => el.removeEventListener('scroll', holdScrollPosition), 150);
            }
          }}
          onInputChange={() => {
            if (onChangeCallback) onChangeCallback();
          }}
          onFocus={() => {
            setIsInputFocused(true);
            onMenuOpen?.();
          }}
          onBlur={() => {
            setIsInputFocused(false);
            onMenuClose?.();
          }}
          onCreateOption={handleCreate}
          {...(loadTagOptions
            ? { loadOptions: debouncedLoadOptions, defaultOptions: true, cacheOptions: true }
            : { options })}
          onKeyDown={(e) => {
            if (e.code === 'Enter') {
              if (tags.some((tag) => tag.value === e.target.value)) {
                e.target.value = '';
              }
            }
          }}
          value={tags}
          menuIsOpen={customContact ? undefined : true}
          menuPortalTarget={customContact ? document.body : undefined}
          menuPosition={customContact ? 'fixed' : undefined}
          menuShouldScrollIntoView={false}
          placeholder={'Type to search or create a new tag'}
          noOptionsMessage={() => null}
          loadingMessage={() => null}
          components={{
            Option,
            MultiValueLabel: CustomMultiValueLabel,
            IndicatorSeparator: () => null,
            DropdownIndicator: () => null,
            ClearIndicator: () => null,
          }}
          styles={colourStyles}
          classNamePrefix={'react-custom-select-tags'}
        />
        {singleLine && canScrollRight && !isInputFocused && (
          <button
            type='button'
            className='general-button tags-scroll-arrow direction-right'
            onClick={() => scrollTags(1)}
          >
            <span className='tags-scroll-arrow-icon' />
          </button>
        )}
      </div>
      {!customContact && (
        <div className='d-flex justify-content-end px-4 px-lg-5 confirm-results-section bg-white rounded-bottom flex-shrink-0'>
          <button
            onClick={() => {
              addTagsToContacts(closeModal);
            }}
            className='general-button action-button px-3 py-2'
            disabled={isLoading || tags.length === 0}
          >
            {isLoading ? (
              <i
                className='fas fa-spinner fa-spin'
                style={{
                  margin: '0 auto',
                  color: '#fcfcfc',
                  marginTop: '2px',
                  width: '33.7px',
                }}
              ></i>
            ) : (
              `Tag ${contactsCount} ${unitLabel}${contactsCount > 1 ? 's' : ''}`
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export { AddTagsContent };
export const defaultRenderTag = (props) => {
  let { tag, key, disabled, onRemove, classNameRemove, getTagDisplayValue, ...other } = props;
  return (
    <span
      key={key}
      {...other}
      style={{
        color: `#00122B`,
        paddingRight: '25px',
      }}
    >
      {getTagDisplayValue(tag)}
      {!disabled && <button className={`${classNameRemove} general-button`} onClick={(e) => onRemove(key)} />}
    </span>
  );
};
export default AddTagsModal;
